import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../services/supabase';
import { getHowieApiUrl } from '../../../config/howieService';
import type {
  HowieGraphData,
  DialecticPole,
  CanonicalPoleInfo,
  CanonicalLink,
  RelatedConcept,
  FrameworkAssociation,
  Canonical,
  Paradigm,
  CoreComponent,
  Dialectic,
  Framework,
  OntologyRelationship,
} from '../types';


const HOWIE_API_URL = getHowieApiUrl();

/** Normalize a string for fuzzy name matching (lowercase, collapse whitespace). */
function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function useHowieGraphData(): HowieGraphData {
  const [paradigms, setParadigms] = useState<Paradigm[]>([]);
  const [components, setComponents] = useState<CoreComponent[]>([]);
  const [dialectics, setDialectics] = useState<Dialectic[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [canonicals, setCanonicals] = useState<Canonical[]>([]);
  const [relationships, setRelationships] = useState<OntologyRelationship[]>([]);
  const [dialecticPoles, setDialecticPoles] = useState<DialecticPole[]>([]);

  const [canonicalPoles, setCanonicalPoles] = useState<Map<string, CanonicalPoleInfo[]>>(
    () => new Map<string, CanonicalPoleInfo[]>(),
  );
  const [canonicalDomainIndex, setCanonicalDomainIndex] = useState<Map<string, number>>(
    () => new Map<string, number>(),
  );
  const [canonicalLinks, setCanonicalLinks] = useState<CanonicalLink[]>([]);
  const [relatedConcepts, setRelatedConcepts] = useState<Map<string, RelatedConcept[]>>(
    () => new Map(),
  );
  const [frameworksByCanonical, setFrameworksByCanonical] = useState<Map<string, FrameworkAssociation[]>>(
    () => new Map(),
  );
  const [paradigmIndex, setParadigmIndex] = useState<Map<string, number>>(
    () => new Map<string, number>(),
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // ── 1. Fire all fetches in parallel ────────────────────────────────────
      const [
        paradigmsRes,
        componentsRes,
        dialecticsRes,
        frameworksRes,
        canonicalsRes,
        relationshipsRes,
        polesRes,
        domainsRes,
      ] = await Promise.all([
        supabase.from('howie_paradigms').select('*').order('name'),
        supabase.from('howie_core_components').select('*').order('name'),
        supabase.from('howie_dialectics').select('*').order('name'),
        supabase.from('howie_frameworks').select('*').order('name'),
        supabase
          .from('howie_canonicals')
          .select('*')
          .order('importance_score', { ascending: false }),
        // Supabase caps at 1000 rows per request — fetch in pages
        Promise.all([
          supabase.from('howie_ontology_relationships').select('*').range(0, 999),
          supabase.from('howie_ontology_relationships').select('*').range(1000, 1999),
          supabase.from('howie_ontology_relationships').select('*').range(2000, 2999),
          supabase.from('howie_ontology_relationships').select('*').range(3000, 3999),
          supabase.from('howie_ontology_relationships').select('*').range(4000, 4999),
        ]).then((pages) => ({
          data: pages.flatMap((p) => p.data ?? []),
          error: pages.find((p) => p.error)?.error ?? null,
        })),
        fetch(`${HOWIE_API_URL}/graph/dialectic-poles`)
          .then((r) => r.json() as Promise<{ poles: DialecticPole[]; error?: string }>)
          .catch(() => ({ poles: [] as DialecticPole[] })),
        fetch(`${HOWIE_API_URL}/graph/canonical-domains`)
          .then((r) => r.json() as Promise<{ canonicals: Array<{ canonical_id: string; canonical_name: string; domain: string }>; error?: string }>)
          .catch(() => ({ canonicals: [] })),
      ]);

      // ── 2. Unpack Supabase results, tolerating individual failures ──────────
      if (paradigmsRes.error) {
        console.error('[useHowieGraphData] howie_paradigms error:', paradigmsRes.error);
      }
      if (componentsRes.error) {
        console.error('[useHowieGraphData] howie_core_components error:', componentsRes.error);
      }
      if (dialecticsRes.error) {
        console.error('[useHowieGraphData] howie_dialectics error:', dialecticsRes.error);
      }
      if (frameworksRes.error) {
        console.error('[useHowieGraphData] howie_frameworks error:', frameworksRes.error);
      }
      if (canonicalsRes.error) {
        console.error('[useHowieGraphData] howie_canonicals error:', canonicalsRes.error);
      }
      if (relationshipsRes.error) {
        console.error(
          '[useHowieGraphData] howie_ontology_relationships error:',
          relationshipsRes.error,
        );
      }

      const rawParadigms: Paradigm[] = paradigmsRes.data ?? [];
      const rawComponents: CoreComponent[] = componentsRes.data ?? [];
      const rawDialectics: Dialectic[] = dialecticsRes.data ?? [];
      const rawFrameworks: Framework[] = frameworksRes.data ?? [];
      const rawCanonicals: Canonical[] = canonicalsRes.data ?? [];
      const rawRelationships: OntologyRelationship[] = relationshipsRes.data ?? [];
      const rawPoles: DialecticPole[] = polesRes.poles ?? [];
      const rawDomains: Array<{ canonical_id: string; canonical_name: string; domain: string }> =
        domainsRes.canonicals ?? [];

      // ── Diagnostic logs ─────────────────────────────────────────────────────
      console.log('[HowieGraph] Loaded:', {
        paradigms: rawParadigms.length,
        components: rawComponents.length,
        dialectics: rawDialectics.length,
        canonicals: rawCanonicals.length,
        relationships: rawRelationships.length,
        dialecticPoles: rawPoles.length,
        canonicalDomains: rawDomains.length,
      });

      // ── 3. Derived maps ─────────────────────────────────────────────────────

      // paradigmIndex: paradigms sorted by name → index 0/1/2 for color lookup
      const newParadigmIndex = new Map<string, number>(
        rawParadigms.map((p, i) => [p.id, i]),
      );

      // Canonical lookup helpers
      const canonicalById = new Map<string, Canonical>(rawCanonicals.map((c) => [c.id, c]));
      const canonicalByName = new Map<string, Canonical>(
        rawCanonicals.map((c) => [normalizeName(c.name), c]),
      );

      // Dialectic lookup by normalized name (poles arrive with name, not UUID)
      const dialecticByName = new Map<string, Dialectic>(
        rawDialectics.map((d) => [normalizeName(d.name), d]),
      );

      // canonicalDomainIndex: Map<canonicalId, domainIndex 0–5>
      // Neo4j domains sorted alphabetically: BEHAVIOR=0, CULTURE=1, LEADERSHIP=2, SELF=3, SOCIETY=4, TRUST=5
      const sortedDomains = [...new Set(rawDomains.map((d) => d.domain))].sort();
      const domainToIndex = new Map(sortedDomains.map((d, i) => [d, i]));
      const newCanonicalDomainIndex = new Map<string, number>();
      for (const domainRow of rawDomains) {
        const canonical = canonicalById.get(domainRow.canonical_id)
          ?? canonicalByName.get(normalizeName(domainRow.canonical_name));
        if (!canonical) continue;
        const dIdx = domainToIndex.get(domainRow.domain);
        if (dIdx !== undefined) {
          newCanonicalDomainIndex.set(canonical.id, dIdx);
        }
      }
      console.log('[HowieGraph] domains found:', sortedDomains, '| mapped:', newCanonicalDomainIndex.size, 'canonicals');

      // canonicalPoles: Map<canonicalId, CanonicalPoleInfo[]>
      // Try canonical_id first (Neo4j UUID), fall back to name match
      const newCanonicalPoles = new Map<string, CanonicalPoleInfo[]>();
      for (const pole of rawPoles) {
        let canonical = canonicalById.get(pole.canonical_id);
        if (!canonical) canonical = canonicalByName.get(normalizeName(pole.canonical_name));
        if (!canonical) continue;

        const dialectic = dialecticByName.get(normalizeName(pole.dialectic_name));
        if (!dialectic) continue;

        const poleInfo: CanonicalPoleInfo = {
          dialectic,
          pole_position: pole.pole_position,
          pole_name: pole.pole_name,
        };

        const existing = newCanonicalPoles.get(canonical.id);
        if (existing) {
          existing.push(poleInfo);
        } else {
          newCanonicalPoles.set(canonical.id, [poleInfo]);
        }
      }

      // canonicalLinks: canonical→canonical relationships from Supabase
      // Derived through CorridorModules, so both A→B and B→A may exist as separate rows.
      // Deduplicate: keep one link per (source, target) pair (sorted to collapse bidirectional).
      // For the panel: keep one entry per (canonical, type) pair.
      const canonicalRelTypes = new Set(['RESONATES_WITH', 'ENABLES', 'DEVELOPS', 'CONTRADICTS', 'BALANCES']);

      // Step 1: collect raw edges
      const rawEdges: Array<{ sourceId: string; targetId: string; type: string }> = [];
      for (const rel of rawRelationships) {
        if (rel.source_type === 'canonical' && rel.target_type === 'canonical' && canonicalRelTypes.has(rel.relationship_type)) {
          const source = canonicalById.get(rel.source_id);
          const target = canonicalById.get(rel.target_id);
          if (!source || !target) continue;
          rawEdges.push({ sourceId: rel.source_id, targetId: rel.target_id, type: rel.relationship_type });
        }
      }

      // Step 2: deduplicate links for galaxy (collapse A→B and B→A into one link)
      const linkDedup = new Set<string>();
      const newCanonicalLinks: CanonicalLink[] = [];
      for (const edge of rawEdges) {
        const key = [edge.sourceId, edge.targetId].sort().join('|') + '|' + edge.type;
        if (linkDedup.has(key)) continue;
        linkDedup.add(key);
        newCanonicalLinks.push({ source: edge.sourceId, target: edge.targetId, type: edge.type });
      }

      // Step 3: build relatedConcepts for panel (deduplicate by canonical+type)
      const newRelatedConcepts = new Map<string, RelatedConcept[]>();
      for (const edge of rawEdges) {
        // Add target as related to source
        const srcList = newRelatedConcepts.get(edge.sourceId) ?? [];
        if (!srcList.some((rc) => rc.canonical.id === edge.targetId && rc.type === edge.type)) {
          srcList.push({ canonical: canonicalById.get(edge.targetId)!, type: edge.type, direction: 'outgoing' });
          newRelatedConcepts.set(edge.sourceId, srcList);
        }
        // Add source as related to target (bidirectional)
        const tgtList = newRelatedConcepts.get(edge.targetId) ?? [];
        if (!tgtList.some((rc) => rc.canonical.id === edge.sourceId && rc.type === edge.type)) {
          tgtList.push({ canonical: canonicalById.get(edge.sourceId)!, type: edge.type, direction: 'incoming' });
          newRelatedConcepts.set(edge.targetId, tgtList);
        }
      }
      console.log('[HowieGraph] canonicalLinks:', newCanonicalLinks.length, '(deduped from', rawEdges.length, 'raw) | relatedConcepts:', newRelatedConcepts.size, 'canonicals with relations');

      // frameworksByCanonical: canonical→framework relationships (ADDRESSES, OPERATIONALIZES, etc.)
      const frameworkById = new Map<string, Framework>(rawFrameworks.map((f) => [f.id, f]));
      const newFrameworksByCanonical = new Map<string, FrameworkAssociation[]>();
      for (const rel of rawRelationships) {
        if (rel.source_type === 'canonical' && rel.target_type === 'framework') {
          const framework = frameworkById.get(rel.target_id);
          if (!framework) continue;
          const list = newFrameworksByCanonical.get(rel.source_id) ?? [];
          list.push({ framework, type: rel.relationship_type });
          newFrameworksByCanonical.set(rel.source_id, list);
        }
      }
      console.log('[HowieGraph] frameworksByCanonical:', newFrameworksByCanonical.size, 'canonicals with framework links');

      // ── 4. Commit all state at once ─────────────────────────────────────────
      setParadigms(rawParadigms);
      setComponents(rawComponents);
      setDialectics(rawDialectics);
      setFrameworks(rawFrameworks);
      setCanonicals(rawCanonicals);
      setRelationships(rawRelationships);
      setDialecticPoles(rawPoles);
      setParadigmIndex(newParadigmIndex);
      setCanonicalDomainIndex(newCanonicalDomainIndex);
      setCanonicalPoles(newCanonicalPoles);
      setCanonicalLinks(newCanonicalLinks);
      setRelatedConcepts(newRelatedConcepts);
      setFrameworksByCanonical(newFrameworksByCanonical);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[useHowieGraphData] Unexpected error during fetch:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    paradigms,
    components,
    dialectics,
    frameworks,
    canonicals,
    relationships,
    dialecticPoles,
    canonicalPoles,
    canonicalDomainIndex,
    canonicalLinks,
    relatedConcepts,
    frameworksByCanonical,
    paradigmIndex,
    loading,
    error,
  };
}
